<template>
  <div>
    <section class="pt-20 lg:pt-25 pb-15">
      <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
        <div class="flex flex-wrap items-center justify-between gap-8 mb-6" v-if="news.data && news.data.length">
          <div class="ribbon font-roboto-slab">Informasi</div>
        </div>
        <div>
          <!-- Blog Categories Tab End -->

          <!-- Blog Categories Content Start -->
          <div>
            <!-- All content start -->
            <div>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-11 gap-x-8">
                <!-- blog item -->

                <template v-for="item in news.data" :key="item.id">
                  <div
                    class="group max-w-md w-full flex flex-col bg-white shadow-lg p-2.5 h-full transition-all hover:scale-105">
                    <div class="relative w-full mb-4 overflow-hidden">
                      <Link :href="`/read/${item.slug}`" @click="handleNewsClick(item.slug, $event)">
                      <!-- Single Image -->
                      <div v-if="!Array.isArray(item.image) || item.image.length <= 1">
                        <img :src="getSafeImageSrc(item.image)" :alt="item.title"
                          @load="handleImageLoad($event, item, 0)" @error="handleImageError($event, item, 0)"
                          :class="['news-image object-cover w-full h-48 aspect-[16/9] transition-transform duration-300 hover:scale-105', { 'opacity-0': isImageLoading(item, 0) }]" />
                      </div>

                      <!-- Multiple Images Gallery -->
                      <div v-else class="relative">
                        <!-- Main Image Display -->
                        <div class="relative overflow-hidden">
                          <img :src="getSafeImageSrc(item.image, 0)" :alt="item.title"
                            @error="handleImageError($event, item, 0)"
                            class="news-image object-cover w-full h-48 aspect-[16/9] transition-transform duration-300 hover:scale-105" />

                          <!-- Image Counter Badge -->
                          <div
                            class="absolute flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-full gallery-counter top-2 right-2 bg-black/70">
                            <i class="fa-solid fa-images"></i>
                            <span>{{ item.image.length }}</span>
                          </div>

                          <!-- Gallery Indicator -->
                          <div class="absolute gallery-indicators bottom-2 left-2 right-2">
                            <div class="flex justify-center space-x-1">
                              <div v-for="(img, index) in item.image.slice(0, 5)" :key="index"
                                class="w-2 h-2 transition-all duration-200 rounded-full"
                                :class="index === 0 ? 'bg-white' : 'bg-white/50'">
                              </div>
                              <div v-if="item.image.length > 5" class="w-2 h-2 rounded-full bg-white/30">
                              </div>
                            </div>
                          </div>

                          <!-- Hover Preview Grid -->
                          <div
                            class="absolute inset-0 flex items-center justify-center p-4 transition-opacity duration-300 opacity-0 gallery-preview bg-black/80 group-hover:opacity-100">
                            <div class="grid max-w-full max-h-full grid-cols-2 gap-1">
                              <div v-for="(img, index) in item.image.slice(0, 4)" :key="index"
                                class="relative overflow-hidden rounded aspect-square">
                                <img :src="getSafeImageSrc(img)" :alt="`${item.title} - Image ${index + 1}`"
                                  @error="handleImageError($event, item, index)"
                                  class="object-cover w-full h-full transition-transform duration-200 hover:scale-105" />
                                <!-- Show "+" overlay for more images -->
                                <div v-if="index === 3 && item.image.length > 4"
                                  class="absolute inset-0 flex items-center justify-center text-lg font-bold text-white bg-black/60 backdrop-blur-sm">
                                  +{{ item.image.length - 4 }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      </Link>
                    </div>
                    <div class="flex flex-col w-full">
                      <h2 class="mb-2 font-semibold text-custom-lg text-dark mobile-font">
                        <Link :href="`/read/${item.slug}`" class="block" @click="handleNewsClick(item.slug, $event)">
                        <span
                          class="bg-gradient-to-r from-primary/50 to-primary/40 bg-[length:0px_10px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 hover:bg-[length:100%_3px] group-hover:bg-[length:100%_10px]">
                          {{ item.title }}
                        </span>
                        </Link>
                      </h2>
                      <div class="flex items-center justify-between gap-3 mt-2">
                        <div class="flex items-center gap-2.5">
                          <span class="flex items-center gap-1.5 text-sm text-gray-500 mobile-font">
                            <i class="fa-solid fa-calendar-days"></i>
                          </span>
                          <p class="text-sm">
                            {{ formatTanggal(item.publish_date) }}
                          </p>
                        </div>

                        <!-- View Count -->
                        <div class="flex items-center gap-2 text-sm text-gray-500">
                          <i class="fa-solid fa-eye" :class="{ 'animate-pulse': isTracking }"></i>
                          <span>{{ item.formatted_view_count || '0' }}</span>
                        </div>
                      </div>

                      <!-- Social Media Share Buttons -->
                      <div class="flex items-center justify-between gap-3 mt-2">
                        <div class="flex items-center gap-3">
                          <span class="text-xs text-gray-500">Bagikan:</span>
                          <div class="flex space-x-2">
                            <a :href="facebookShare(item)" target="_blank" rel="noopener noreferrer nofollow"
                              class="text-blue-600 transition-colors hover:text-blue-800"
                              aria-label="Bagikan ke Facebook" title="Bagikan ke Facebook">
                              <i class="fa-brands fa-facebook-f" aria-hidden="true"></i>
                            </a>
                            <a :href="whatsappShare(item)" target="_blank" rel="noopener noreferrer nofollow"
                              class="text-green-600 transition-colors hover:text-green-800"
                              aria-label="Bagikan ke WhatsApp" title="Bagikan ke WhatsApp">
                              <i class="fa-brands fa-whatsapp" aria-hidden="true"></i>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Culture content end -->
          </div>
          <div class="flex justify-center mt-8 sm:justify-end" v-if="news.data.length">
            <Link href="/informasi"
              class="inline-flex items-center justify-center w-full px-8 py-3 text-base font-semibold text-white transition-all duration-300 ease-in-out transform bg-red-900 sm:w-auto group hover:bg-red-800 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 active:scale-95">
            Selebihnya
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
              class="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1">
              <path fill-rule="evenodd"
                d="M16.72 7.72a.75.75 0 0 1 1.06 0l3.75 3.75a.75.75 0 0 1 0 1.06l-3.75 3.75a.75.75 0 1 1-1.06-1.06L19.19 12l-2.47-2.47a.75.75 0 0 1 0-1.06Zm-9.44 0a.75.75 0 0 1 0 1.06L4.81 12l2.47 2.47a.75.75 0 1 1-1.06 1.06L2.47 12.53a.75.75 0 0 1 0-1.06l3.75-3.75a.75.75 0 0 1 1.06 0Z"
                clip-rule="evenodd" />
            </svg>
            </Link>
          </div>
          <!-- Blog Categories Content End -->
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted } from "vue";
import { Link, router } from "@inertiajs/vue3";
import { useNewsTracking } from "@/composables/useNewsTracking.js";
import dayjs from "dayjs";
import "dayjs/locale/id";
dayjs.locale("id");

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
});

// Use composable for news tracking
const { isTracking, trackingErrors, trackNewsView } = useNewsTracking();

// Image loading state
const imageLoadingStates = ref(new Map());

const formatTanggal = (tanggal) => {
  return tanggal ? dayjs(tanggal).format("D MMMM YYYY") : null;
};

// SSR-safe base URL and share link builders
const baseUrl = ref("");
onMounted(() => {
  if (typeof window !== "undefined" && window.location) {
    baseUrl.value = window.location.origin;
  }
});

const buildReadUrl = (slug) => {
  // Fallback to relative URL during SSR; becomes absolute after mounted
  return `${baseUrl.value ? baseUrl.value : ""}/read/${slug}`;
};

const buildShareUrl = (slug) => {
  return `${baseUrl.value ? baseUrl.value : ""}/share/${slug}`;
};

const facebookShare = (item) => {
  const url = encodeURIComponent(buildShareUrl(item.slug));
  return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
};

const whatsappShare = (item) => {
  const text = encodeURIComponent(`${item.title} ${buildShareUrl(item.slug)}`);
  return `https://wa.me/?text=${text}`;
};

// Enhanced click handler with Vue-style event handling
const handleNewsClick = (slug, event) => {
  // Optional: Add visual feedback
  const target = event.currentTarget;
  target.style.opacity = '0.8';
  setTimeout(() => {
    target.style.opacity = '1';
  }, 150);

  // Track view asynchronously
  trackNewsView(slug);
};

// Image loading handlers
const handleImageLoad = (event, item, index = 0) => {
  const key = `${item.id}-${index}`;
  imageLoadingStates.value.set(key, 'loaded');
};

const handleImageError = (event, item, index = 0) => {
  console.warn(`Failed to load image for news: ${item.title}`);
  const key = `${item.id}-${index}`;
  imageLoadingStates.value.set(key, 'error');

  // Try to use a fallback image or show a placeholder
  const img = event.target;
  img.src = '/images/news-placeholder.jpg'; // fallback image

  // If fallback also fails, show a colored placeholder
  img.onerror = () => {
    img.style.display = 'none';
    const placeholder = img.parentElement.querySelector('.image-placeholder') ||
      document.createElement('div');
    placeholder.className = 'image-placeholder w-full h-48 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-500';
    placeholder.innerHTML = '<i class="text-3xl fa-solid fa-image"></i>';
    if (!img.parentElement.querySelector('.image-placeholder')) {
      img.parentElement.appendChild(placeholder);
    }
  };
};

const isImageLoading = (item, index = 0) => {
  const key = `${item.id}-${index}`;
  return !imageLoadingStates.value.has(key) || imageLoadingStates.value.get(key) === 'loading';
};

// Get safe image source
const getSafeImageSrc = (imageData, index = 0) => {
  if (Array.isArray(imageData)) {
    return imageData[index] || imageData[0] || '/images/news-placeholder.jpg';
  }
  return imageData || '/images/news-placeholder.jpg';
};

// Alternative: Using Inertia router visit with tracking
const visitNewsWithTracking = (slug) => {
  // Track view first
  trackNewsView(slug);

  // Then navigate
  router.visit(`/read/${slug}`, {
    onStart: () => {
      console.log('🚀 Navigating to news:', slug);
    },
    onFinish: () => {
      console.log('✅ Navigation completed');
    }
  });
};
</script>

<style scoped>
.ribbon {
  background: linear-gradient(135deg,
      #1e3a8a 0%,
      /* indigo-800 */
      #2563eb 35%,
      /* blue-600 */
      #3b82f6 70%,
      /* blue-500 */
      #93c5fd 100%
      /* blue-300 */
    );
  color: #fff;
  font-weight: bold;
  font-size: 1.5rem;
  padding: 0.75rem 2rem;
  border-radius: 0;
  margin: 0 auto 2rem auto;
  width: fit-content;
  box-shadow: 0 8px 25px rgba(37, 99, 235, 0.28),
    0 4px 12px rgba(59, 130, 246, 0.2);
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.ribbon::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent);
  transition: left 0.5s ease;
}

.ribbon:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(37, 99, 235, 0.38),
    0 6px 18px rgba(59, 130, 246, 0.28);
}

.ribbon:hover::before {
  left: 100%;
}

.icon {
  display: inline-block;
  vertical-align: middle;
  margin-right: 0.5rem;
  width: 1.2em;
  height: 1.2em;
  color: #2563eb;
}

/* Mobile-specific font styling */
.mobile-font {
  /* Mobile font (default) */
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
  font-weight: 500;
}

/* Desktop font styling */
@media (min-width: 640px) {
  .mobile-font {
    font-family: "Roboto", "Segoe UI", -apple-system, BlinkMacSystemFont,
      sans-serif;
    font-weight: inherit;
  }
}

/* View count styling */
.fa-eye {
  font-size: 0.85em;
  opacity: 0.7;
}

/* Gallery styles */
.gallery-preview {
  backdrop-filter: blur(2px);
}

.gallery-counter {
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.gallery-indicators {
  backdrop-filter: blur(4px);
}

/* Smooth transitions for gallery elements */
.gallery-preview img {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.gallery-preview img:hover {
  transform: scale(1.05);
}

/* Image loading and error states */
.news-image {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  background-size: 400% 400%;
  animation: shimmer 2s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

/* Image placeholder styles */
.image-placeholder {
  position: relative;
  overflow: hidden;
}

.image-placeholder::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent);
  animation: placeholder-shimmer 1.5s infinite;
}

@keyframes placeholder-shimmer {
  0% {
    left: -100%;
  }

  100% {
    left: 100%;
  }
}

/* Responsive adjustments for multiple images */
@media (max-width: 640px) {
  .gallery-preview .grid {
    grid-template-columns: 1fr 1fr;
    gap: 0.25rem;
  }

  .gallery-counter {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }

  .gallery-indicators .w-2 {
    width: 0.375rem;
    height: 0.375rem;
  }
}
</style>
